import React, { useState, useEffect } from 'react';
import { Feature, Product } from '../../types';
import { apiRequest } from '../../utils/api';
import { showTempMessage, AppMessage, MessageSetter } from '../../utils/messages';
import '../../App.css';

interface CreateFeatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string;
  products: Product[];
  onFeatureCreated: (feature: Feature) => void;
  setMessage: MessageSetter;
  showMessage: (msg: AppMessage) => void;
  editingFeature?: Feature | null;
  onFeatureUpdated?: (feature: Feature) => void;
}

const CreateFeatureModal: React.FC<CreateFeatureModalProps> = ({
  isOpen,
  onClose,
  productId,
  products,
  onFeatureCreated,
  setMessage,
  showMessage,
  editingFeature,
  onFeatureUpdated
}) => {
  const [featureName, setFeatureName] = useState('');
  const [featureDescription, setFeatureDescription] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(productId);

  // Update selected product when productId prop changes
  useEffect(() => {
    if (productId) {
      setSelectedProductId(productId);
    }
  }, [productId]);

  // Reset form when modal opens/closes or when editing feature changes
  useEffect(() => {
    if (isOpen) {
      if (editingFeature) {
        setFeatureName(editingFeature.name || '');
        setFeatureDescription(editingFeature.description || '');
        setSelectedProductId(editingFeature.productId || productId || '');
      } else {
        setFeatureName('');
        setFeatureDescription('');
        setSelectedProductId(productId || '');
      }
    }
  }, [isOpen, productId, editingFeature]);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleCreate = async () => {
    if (!featureName.trim()) {
      showTempMessage(setMessage, { type: 'error', text: 'Feature name is required' });
      return;
    }
    if (!selectedProductId) {
      showTempMessage(setMessage, { type: 'error', text: 'Please select a product' });
      return;
    }

    try {
      if (editingFeature) {
        // Update existing feature
        const updatedFeature = await apiRequest<Feature>(`/api/features/${editingFeature.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: featureName.trim(),
            description: featureDescription.trim() || undefined
          }),
          headers: { 'Content-Type': 'application/json' }
        }, { useAuth: true });

        showTempMessage(setMessage, { type: 'success', text: 'Feature updated successfully' });
        if (onFeatureUpdated) {
          onFeatureUpdated(updatedFeature);
        }
        setFeatureName('');
        setFeatureDescription('');
        onClose();
      } else {
        // Create new feature
        const newFeature = await apiRequest<Feature>('/api/features', {
          method: 'POST',
          body: JSON.stringify({
            name: featureName.trim(),
            productId: selectedProductId,
            description: featureDescription.trim() || undefined
          }),
          headers: { 'Content-Type': 'application/json' }
        }, { useAuth: true });

        showTempMessage(setMessage, { type: 'success', text: 'Feature created successfully' });
        onFeatureCreated(newFeature);
        setFeatureName('');
        setFeatureDescription('');
        onClose();
      }
    } catch (error: any) {
      showTempMessage(setMessage, { type: 'error', text: error.message || (editingFeature ? 'Failed to update feature' : 'Failed to create feature') });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <div
        className="card"
        style={{ maxWidth: '500px', width: '90%', zIndex: 1001 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 className="section-title">{editingFeature ? 'Edit Feature' : 'Create New Feature'}</h3>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon"
            style={{ fontSize: '1.5rem' }}
          >
            ×
          </button>
        </div>

        {!editingFeature && (
          <div className="form-group">
            <label>Product *</label>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              className="form-control"
              disabled={!!productId} // Disable if productId is provided (locked to that product)
            >
              <option value="">Select a product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>{product.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label>Feature Name *</label>
          <input
            type="text"
            value={featureName}
            onChange={(e) => setFeatureName(e.target.value)}
            placeholder="Enter feature name"
            className="form-control"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && featureName.trim() && selectedProductId) {
                e.preventDefault();
                handleCreate();
              }
            }}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={featureDescription}
            onChange={(e) => setFeatureDescription(e.target.value)}
            placeholder="Enter feature description"
            rows={3}
            className="form-control"
          />
        </div>

        <div className="button-group" style={{ marginTop: '1.5rem' }}>
          <button
            onClick={handleCreate}
            disabled={!featureName.trim() || !selectedProductId}
            className="btn btn-primary"
            style={{
              opacity: (featureName.trim() && selectedProductId) ? 1 : 0.5,
              cursor: (featureName.trim() && selectedProductId) ? 'pointer' : 'not-allowed'
            }}
          >
            {editingFeature ? 'Update' : 'Create'}
          </button>
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFeatureModal;

